<!--
  The Insights filter bar. This is a port of CGS2022 insightsHeaderExpanded.vue, restyled to the
  approved mockup: every filter on one line (Time Frame and Trend Time Frame first), Load insight
  pinned top right, every dropdown searchable, and the applied selections as removable chips.

  What stays exactly as CGS has it
    - The option lists, the date presets, and how picking a Time Frame moves the Trend Time
      Frame (helpers/insights/insightDates.js).
    - The event. "Load insight" emits `apply` with the payload onInsightChange() emitted
      (helpers/insights/insightFilters.js buildInsightChange), so each page's queryChanged(e) is
      carried across unchanged.
    - The branch, LO and BDA lists come from GetInsightHeader for the acting user, and BDAs
      without an encompassId are dropped.
    - The corp-only warning when a window is longer than two years.
    - Per-page filter visibility (visibleFilters), and Revenue Analysis starting on First Lien.

  What changed
    - "All" is an empty selection rather than an item in each list, so there's no
      All-exclusivity watcher to get wrong. An empty list sends [] exactly as "All" did.
    - Nothing loads until the user presses Load insight. CGS works the same way. The difference
      is that the bar says when the selections have changed since the last load.

  Props
    title               CGS's insightsTitle (some filter rules key on it)
    timeFrame           'today' | 'month' | ''  (CGS's time-frame prop)
    timeFrameVisible    false on the snapshot pages that called hideTimeFrame
    showTrend           false where CGS hides the Trend Time Frame (Dialpad)
    hide / show         filter keys to force off / on for this page
    loading             disables Load while a query runs
-->
<template>
  <section class="ins-card ins-filters" aria-label="Filters">
    <!-- One line: every filter, then Load insight pinned top right. Filters wrap only when the
         window is too narrow for them; the button stays on the first line. -->
    <div class="ins-filters__line">
      <div class="ins-filters__fields">
        <template v-if="timeFrameVisible">
          <div class="ins-field">
            <span>Time frame</span>
            <SearchSelect label="Time frame" :items="presets" :model-value="range" @change="onRange" />
          </div>
          <div v-if="showTrend" class="ins-field">
            <span>Trend time frame</span>
            <SearchSelect label="Trend time frame" :items="presets" :model-value="trend" @change="onTrend" />
          </div>
        </template>

        <div v-for="f in defs" :key="f.key" class="ins-field">
          <span>{{ f.label }}</span>
          <PickList v-bind="pickProps(f.key)" :label="f.label" :model-value="sel[f.key]" @change="v => onSelect(f.key, v)" />
        </div>
      </div>

      <div class="ins-filters__go">
        <button type="button" class="ins-btn ins-btn--primary" :disabled="loading" @click="apply">
          {{ loading ? 'Loading…' : 'Load insight' }}
        </button>
      </div>
    </div>

    <div v-if="timeFrameVisible && (range === 'Other' || trend === 'Other')" class="ins-filters__row" style="margin-top:12px">
      <template v-if="range === 'Other'">
        <div class="ins-field"><span>Start date</span><DatePicker :value="start" @change="setDate('start', $event.value)" /></div>
        <div class="ins-field"><span>End date</span><DatePicker :value="end" @change="setDate('end', $event.value)" /></div>
      </template>
      <template v-if="showTrend && trend === 'Other'">
        <div class="ins-field"><span>Trend start date</span><DatePicker :value="trendStart" @change="setDate('trendStart', $event.value)" /></div>
        <div class="ins-field"><span>Trend end date</span><DatePicker :value="trendEnd" @change="setDate('trendEnd', $event.value)" /></div>
      </template>
    </div>

    <div class="ins-filters__chips">
      <span v-if="timeFrameVisible" class="ins-muted"><b>{{ windowText }}</b><template v-if="showTrend"> vs. {{ trendText }}</template></span>
      <button v-for="c in chips" :key="c.key + c.index" type="button" class="ins-chip" :aria-label="`Remove ${c.label} ${c.text}`" @click="removeChip(c)">
        {{ c.label }}: {{ c.text }} <i class="fa-solid fa-xmark" aria-hidden="true"></i>
      </button>
      <button v-if="chips.length" type="button" class="ins-chip" style="background:transparent" @click="clearAll">Clear all</button>
      <span v-if="dirty" class="ins-muted" style="margin-left:auto"><i class="fa-light fa-circle-info"></i> Changes not loaded yet</span>
      <span v-else-if="loadedAt" class="ins-muted" style="margin-left:auto">Loaded {{ loadedAt }}</span>
    </div>

    <div v-if="longWindow" class="ins-banner ins-banner--warn" style="margin-top:12px">
      <i class="fa-solid fa-circle-exclamation"></i>
      <div>A window longer than two years can take a while to load.</div>
    </div>
  </section>
</template>

<script>
import PickList from '@/components/insightsKit/PickList.vue'
import SearchSelect from './SearchSelect.vue'
import { DatePicker } from '@progress/kendo-vue-dateinputs'
import insightsApi, { actingAzureId } from '@/api/insights.service.js'
import { DATE_RANGE_PRESETS, rangeFor, trendPresetFor, defaultWindowFor } from '@/helpers/insights/insightDates.js'
import {
  STATES, CHANNELS, DIVISIONS, LIEN_POSITIONS, AMORTIZATIONS, RATE_LOCKS, WAREHOUSE_LINES,
  visibleFilters, emptySelections, buildInsightChange, selectionChips, isLongWindow
} from '@/helpers/insights/insightFilters.js'
import { insightGates } from '@/helpers/insights/insightPages.js'

export default {
  name: 'InsightFilterBar',
  components: { PickList, SearchSelect, DatePicker },
  props: {
    title: { type: String, default: '' },
    timeFrame: { type: String, default: '' },
    timeFrameVisible: { type: Boolean, default: true },
    showTrend: { type: Boolean, default: true },
    hide: { type: Array, default: () => [] },
    show: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false }
  },
  emits: ['apply', 'header'],
  data() {
    const w = defaultWindowFor(this.timeFrame)
    return {
      presets: DATE_RANGE_PRESETS,
      range: w.range,
      trend: w.trend,
      start: w.start,
      end: w.end,
      trendStart: w.trendStart,
      trendEnd: w.trendEnd,
      sel: emptySelections(),
      lists: {
        division: DIVISIONS, branch: [], lo: [], channel: CHANNELS, bda: [], state: STATES,
        lien: LIEN_POSITIONS, amortization: AMORTIZATIONS, lock: RATE_LOCKS, warehouseLine: WAREHOUSE_LINES
      },
      appliedSig: '',
      loadedAt: '',
      isCorp: insightGates().corp
    }
  },
  computed: {
    defs() { return visibleFilters({ title: this.title, timeFrameVisible: this.timeFrameVisible, hide: this.hide, show: this.show }) },
    chips() { return selectionChips(this.sel, this.defs) },
    windowText() { return this.span(this.range, this.start, this.end) },
    trendText() { return this.span(this.trend, this.trendStart, this.trendEnd) },
    signature() {
      return JSON.stringify([this.sel, this.start, this.end, this.trendStart, this.trendEnd])
    },
    dirty() { return !!this.appliedSig && this.appliedSig !== this.signature },
    longWindow() {
      if (!this.isCorp || !this.timeFrameVisible) return false
      return isLongWindow(this.start, this.end) || (this.showTrend && isLongWindow(this.trendStart, this.trendEnd))
    }
  },
  created() {
    // Revenue Analysis - Active Locks opens on First Lien in CGS.
    if (this.title === 'Revenue Analysis - Active Locks') this.sel.lien = [LIEN_POSITIONS[0]]
    // The page runs its own default query on mount, as CGS's pages do. Counting that as
    // "applied" keeps the dirty hint quiet until the user changes something.
    this.appliedSig = this.signature
    this.loadedAt = ''
    this.loadHeader()
  },
  methods: {
    async loadHeader() {
      try {
        const res = await insightsApi.getInsightHeader()
        const d = res?.data || {}
        const branches = (d.branchlist || []).slice().sort((a, b) => String(a).localeCompare(String(b)))
        const los = JSON.parse(JSON.stringify(d.lolist || []))
        const bdas = los.filter(e => e.encompassId && String(e.encompassId).trim() !== '')
        this.lists = { ...this.lists, branch: branches, lo: los, bda: bdas }
        this.$emit('header', d)
      } catch (e) {
        // The pages still load with no branch or LO lists. Those filters just stay empty,
        // which CGS also does when this call fails.
        this.$emit('header', null)
      }
    },
    // Each filter is a PickList: a search box at the top, a checkbox list, "All" when nothing is
    // picked. The chips below the bar name each pick.
    pickProps(key) {
      const items = this.lists[key]
      if (key === 'lo' || key === 'bda') return { items, textField: 'displayName', keyField: key === 'lo' ? 'email' : 'encompassId' }
      if (['division', 'lien', 'amortization', 'lock', 'warehouseLine'].includes(key)) return { items, textField: 'display', keyField: 'value' }
      return { items }
    },
    onSelect(key, value) {
      this.sel = { ...this.sel, [key]: Array.isArray(value) ? value : [] }
    },
    onRange(v) {
      this.range = v
      const r = rangeFor(v)
      if (r) { this.start = r.start; this.end = r.end }
      const t = trendPresetFor(v)
      if (t) this.onTrend(t)
    },
    onTrend(v) {
      this.trend = v
      const r = rangeFor(v)
      if (r) { this.trendStart = r.start; this.trendEnd = r.end }
    },
    setDate(which, value) {
      this[which] = value
      if (which === 'start' || which === 'end') this.range = 'Other'
      else this.trend = 'Other'
    },
    removeChip(c) {
      const next = this.sel[c.key].slice()
      next.splice(c.index, 1)
      this.onSelect(c.key, next)
    },
    clearAll() { this.sel = emptySelections() },
    span(preset, s, e) {
      if (preset && preset !== 'Other') return preset
      const f = d => (d instanceof Date && !isNaN(d) ? d.toLocaleDateString() : '?')
      return `${f(s)} – ${f(e)}`
    },
    apply() {
      this.appliedSig = this.signature
      this.loadedAt = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
      this.$emit('apply', buildInsightChange(this.sel, {
        start: this.start, end: this.end, trendStart: this.trendStart, trendEnd: this.trendEnd
      }, actingAzureId()))
    }
  }
}
</script>
